import React from "react";
import { Lightbulb } from "lucide-react";

/**
 * ScoreBoostTips — shown when the rewrite scored under 75. Uses the feature breakdown
 * to surface the two lowest-scoring dimensions and turn them into concrete controls to try.
 */
const TIPS = {
  "Sentence rhythm": {
    title: "Vary sentence lengths",
    action: "Increase rewrite strength to 'strong' — it forces wider length swings.",
  },
  "Length range": {
    title: "Add a very short and a very long sentence",
    action: "Try strength 'strong', or 'longer' so there's room for sprawling sentences alongside short ones.",
  },
  "Vocabulary": {
    title: "Add specific, concrete word choices",
    action: "Pick a specific audience (e.g. 'creators' or 'developers') to pull in richer, less generic vocabulary.",
  },
  "Phrase repetition": {
    title: "Break repeated phrases and openings",
    action: "Bump strength to 'strong' so the rewrite restructures sentences instead of swapping synonyms.",
  },
  "Structure variety": {
    title: "Break structural parallelism",
    action: "Try strength 'strong' — it reorders clauses so no two sentences share the same shape.",
  },
  "Punctuation balance": {
    title: "Ease off dashes and colons",
    action: "Long em-dash chains read as machine-written. Try 'longer' output so ideas get full sentences instead.",
  },
  "AI-phrase avoidance": {
    title: "Kill remaining AI clichés",
    action: "Bump strength to 'strong' — it explicitly blocks phrases like 'delve', 'moreover', 'leverage'.",
  },
  "Unpredictability": {
    title: "Make the wording less predictable",
    action: "Try strength 'strong' — it rebuilds sentences instead of reaching for the phrasing a model picks first.",
  },
  "Surprisal variance": {
    title: "Let quality vary across the piece",
    action: "Try 'longer' output so some passages run plain and others unusual, the way real writing does.",
  },
  "Cliche avoidance": {
    title: "Drop the fake-casual filler",
    action: "Set formality to 'neutral' or 'formal' — casual filler like 'here's the thing' is what detectors flag.",
  },
};

export default function ScoreBoostTips({ data, isAuthed }) {
  if (!data || typeof data.score !== "number" || data.score >= 75) return null;
  const breakdown = data.breakdown || [];
  const weakest = [...breakdown].sort((a, b) => a.value - b.value).slice(0, 2);
  const tips = weakest.map((w) => TIPS[w.label]).filter(Boolean);
  if (tips.length === 0) return null;

  return (
    <div data-testid="score-boost-tips" className="mt-4 rounded-xl p-4 border border-[hsl(var(--accent))]/20 bg-[hsl(var(--accent))]/[0.06]">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 bg-[hsl(var(--accent))]/15">
          <Lightbulb size={16} className="text-[hsl(var(--accent))]" />
        </div>
        <div className="flex-1">
          <div className="font-semibold text-sm">Push this rewrite higher</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))] mb-3">Two quick tweaks that would boost your score:</div>
          <ul className="space-y-2">
            {tips.map((tip, i) => (
              <li key={i} className="text-sm flex gap-2">
                <span className="text-[hsl(var(--accent))] font-bold mt-0.5">→</span>
                <span>
                  <span className="font-semibold">{tip.title}. </span>
                  <span className="text-[hsl(var(--muted-foreground))]">{tip.action}</span>
                </span>
              </li>
            ))}
          </ul>
          {!isAuthed && (
            <div className="text-xs text-[hsl(var(--muted-foreground))] mt-3">
              Signed-in users can save personal style profiles for even higher scores.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
